<template>
  <div
    v-if="visible"
    class="context-menu fixed-container"
    :style="styleObject"
    ref="contextMenu"
  >
    <ul role="menu">
      <li role="menuitem" @click="seeProfile">
        <BaseIcon iconName="settings" class="item-main-icon" :width="iconSize" :height="iconSize">
        </BaseIcon>
        {{ $t('userMenu.profileSettings') }}
      </li>

      <li role="menuitem" @click="openSupportPopup">
        <BaseIcon iconName="help" class="item-main-icon" :width="iconSize" :height="iconSize">
        </BaseIcon>
        {{ $t('userMenu.support') }}
      </li>

      <li role="menuitem" class="locked" aria-disabled="true">
        <BaseIcon
          iconName="credit"
          class="item-main-icon"
          :width="iconSize"
          :height="iconSize"
          color="var(--color-white-40)"
        >
        </BaseIcon>
        {{ $t('userMenu.buyCredits') }}
        <BaseIcon
          iconName="lock"
          class="lock-icon"
          :width="iconSize"
          :height="iconSize"
          color="var(--color-white-40)"
        >
        </BaseIcon>
      </li>

      <li role="menuitem" @click="seePlans">
        <BaseIcon iconName="barGroup" class="item-main-icon" :width="iconSize" :height="iconSize">
        </BaseIcon>
        {{ $t('userMenu.seePlans') }}
      </li>

      <li role="menuitem" @click="handleLogout" class="logout-item">
        <BaseIcon iconName="logout" class="item-main-icon" :width="iconSize" :height="iconSize">
        </BaseIcon>
        {{ $t('profile.logout') }}
      </li>
    </ul>
  </div>

  <SupportPopup :show="showSupportPopup" @close="showSupportPopup = false" />
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, nextTick } from "vue";
// import { useStore } from "vuex";
import { useRouter } from "vue-router";
import BaseIcon from "@/components/BaseComponents/BaseIcon.vue";
import SupportPopup from "./SupportPopup.vue";
import { useBreakpoint } from "@/composables/useBreakpoint";

// const store = useStore();
const router = useRouter();
const visible = ref(false);
const contextMenu = ref(null);
const showSupportPopup = ref(false);

const { isMobile } = useBreakpoint();

const iconSize = computed(() => {
  return isMobile.value ? 20 : 24;
});

const styleObject = reactive({
  position: "fixed",
  zIndex: 500, // matches --z-index-modal
  visibility: "hidden",
});

function showMenu(event) {
  event.stopPropagation();
  const elementRect = event.currentTarget.getBoundingClientRect();
  const viewportMargin = 8; // matches --spacing-viewport-margin

  visible.value = true;
  styleObject.visibility = "hidden";

  nextTick(() => {
    if (!contextMenu.value) {
      console.error("Context menu element is not available.");
      return;
    }

    const menuWidth = contextMenu.value.offsetWidth;
    const menuHeight = contextMenu.value.offsetHeight;

    // Calculate left position (align to right edge of trigger element)
    let left = elementRect.right - menuWidth;

    // Ensure menu doesn't overflow viewport
    if (left < viewportMargin) {
      left = viewportMargin;
    } else if (left + menuWidth > window.innerWidth - viewportMargin) {
      left = window.innerWidth - menuWidth - viewportMargin;
    }

    // Calculate bottom position (position above the trigger)
    let bottom = window.innerHeight - elementRect.top;

    // If menu would overflow at bottom, position it above
    if (menuHeight > elementRect.top - viewportMargin) {
      // Not enough space above, position below the trigger
      bottom = window.innerHeight - elementRect.bottom;
    }

    // Ensure menu doesn't overflow viewport vertically
    if (bottom < viewportMargin) {
      bottom = viewportMargin;
    }

    styleObject.left = `${left}px`;
    styleObject.bottom = `${bottom}px`;
    styleObject.visibility = "visible";
  });
}

function hideMenu() {
  visible.value = false;
}

const emit = defineEmits(['logout']);

function handleLogout() {
  visible.value = false;
  emit('logout');
}

function seePlans() {
  router.push("/plans");
}

function seeProfile() {
  router.push("/profile");
}

function openSupportPopup() {
  showSupportPopup.value = true;
  visible.value = false;
}

function handleClickOutside(event) {
  if (visible.value && contextMenu.value && !contextMenu.value.contains(event.target)) {
    hideMenu();
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});

defineExpose({
  showMenu,
  hideMenu,
});
</script>

<style scoped>
.context-menu {
  background-color: var(--color-white-10);
  backdrop-filter: var(--backdrop-blur);
  border: var(--border-width-thin) solid var(--border-color-default);
  border-radius: var(--border-radius-lg);
  width: var(--width-context-menu);
  max-width: var(--max-width-context-menu);
  position: fixed;
}

@media (max-width: 768px) {
  .context-menu {
    width: var(--width-context-menu-mobile);
    min-width: 200px;
  }
}

.context-menu ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.context-menu li {
  display: flex;
  align-items: center;
  padding: var(--padding-menu-item);
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 768px) {
  .context-menu li {
    padding: var(--padding-md);
    font-size: var(--font-size-caption);
  }
}

.item-main-icon {
  flex-shrink: 0;
  margin-right: var(--gap-xl);
}

.lock-icon {
  margin-left: auto;
}

.logout-item {
  border-top: var(--border-width-thin) solid var(--border-color-default);
}

.locked {
  color: var(--color-white-40);
  cursor: not-allowed !important;
}
</style>
